import { AuthService } from './authService';
import { UserProfileDto, UpdateUserProfileRequest, CreateUserProfileRequest } from '../types/userProfile';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class UserProfileService {

  async getCurrentUserProfile(): Promise<UserProfileDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/user-profiles/current`);

    if (!response.ok) {
      throw new Error(`Failed to get current user profile: ${response.status}`);
    }

    return await response.json();
  }

  async updateCurrentUserProfile(profile: UpdateUserProfileRequest): Promise<UserProfileDto> {
    const response = await AuthService.authenticatedPut(`${API_BASE_URL}/user-profiles/current`, profile);

    if (!response.ok) {
      throw new Error(`Failed to update current user profile: ${response.status}`);
    }

    return await response.json();
  }

  // Admin methods for managing all profiles

  /**
   * Get all profiles for the current customer
   */
  async getAllProfilesForCustomer(): Promise<UserProfileDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/user-profiles/current-customer`);

    if (!response.ok) {
      throw new Error(`Failed to get profiles: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get a specific profile by customer number and user ID
   */
  async getProfile(customerNo: string, userID: string): Promise<UserProfileDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/user-profiles/${customerNo}/${userID}`);

    if (!response.ok) {
      throw new Error(`Failed to get profile: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Create a new profile
   */
  async createProfile(request: CreateUserProfileRequest): Promise<UserProfileDto> {
    const response = await AuthService.authenticatedPost(`${API_BASE_URL}/user-profiles`, request);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to create profile: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Update an existing profile
   */
  async updateProfile(customerNo: string, userID: string, profile: UpdateUserProfileRequest): Promise<UserProfileDto> {
    const response = await AuthService.authenticatedPut(`${API_BASE_URL}/user-profiles/${customerNo}/${userID}`, profile);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update profile: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Delete a profile
   */
  async deleteProfile(customerNo: string, userID: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${API_BASE_URL}/user-profiles/${customerNo}/${userID}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to delete profile: ${response.status}`);
    }
  }

  /**
   * Get all profiles for the current company
   */
  async getAllProfilesForCompany(): Promise<UserProfileDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/user-profiles/current-company`);

    if (!response.ok) {
      throw new Error(`Failed to get profiles: ${response.status}`);
    }

    return await response.json();
  }
}

export const userProfileService = new UserProfileService();
